import React from 'react'

import clsx from 'clsx'
import dayjs from 'dayjs'

import Paper from 'components/Paper'
import Review from 'components/Review'
import { EventContext } from 'context/EventContext'
import useViewport from 'hooks/useViewport'

import AttributeList, { ATTRIBUTE_VARIANTS } from '../AttributeList'
import GridContainer from '../GridContainer'
import GridItem from '../GridItem'
import ReviewsRecommendation from '../ReviewsRecommendation'
import ReviewsByField from './components/ReviewsByField'
import ReviewsSnippets from './components/ReviewsSnippets'

import classes from './UserReviews.module.scss'

/** @type {import("./types").default} */
const Reviews = ({ listings, isMobileTablet, snippetSize = 'small' }) => {
  const gridSize = listings.length
  const { up } = useViewport()
  const showReviewsRecommendation = listings.some(listing => listing.reviews.recommend_percentage)

  return (
    <>
      <EventContext value={{ engagementComponent: 'product_reviews' }}>
        <GridContainer>
          {listings.map((listing, index) => (
            <GridItem key={`user-reviews-content-${listing.id}`}>
              <Paper elevation={0} className={clsx(classes.firstPaper, classes[`firstPaper-${gridSize}`])} square>
                <div className={classes.reviewStarBreakdownContainer}>
                  <Review.StarBreakdown
                    className={isMobileTablet ? classes.reviewStarBreakdownMobile : undefined}
                    href={listing.cta_urls.reviews}
                    ratingAvg={listing.rating_avg}
                    reviewsByRating={listing.reviews.by_rating}
                    totalNumberOfReviews={listing.reviews.total_number}
                    variant={up('lg') ? 'large' : 'small'}
                  />
                </div>
                <ReviewsByField
                  gridIndex={index}
                  gridSize={listings.length}
                  isMobileTablet={isMobileTablet}
                  listing={listing}
                />
                {showReviewsRecommendation && (
                  <ReviewsRecommendation
                    gridIndex={index}
                    gridSize={listings.length}
                    isMobileTablet={isMobileTablet}
                    reviews={listing.reviews}
                    recommendPercentage={listing.reviews.recommend_percentage}
                  />
                )}
              </Paper>
            </GridItem>
          ))}
        </GridContainer>
        <GridContainer>
          {listings.map(listing => {
            return (
              <GridItem className="UserReviews__SnippetPros" key={`user-reviews-content-pros-${listing.id}`}>
                <Paper square className={clsx(classes.paper, classes.noTopPadding)} elevation={0}>
                  <ReviewsSnippets
                    snippets={listing.reviews.snippets.filter(snippet => snippet.type === 'pros')}
                    snippetSize={snippetSize}
                    type="pros"
                  />
                </Paper>
              </GridItem>
            )
          })}
        </GridContainer>
        <GridContainer>
          {listings.map(listing => {
            return (
              <GridItem className="UserReviews__SnippetCons" key={`user-reviews-content-cons-${listing.id}`}>
                <Paper square className={clsx(classes.paper, classes.noTopPadding)} elevation={0}>
                  <ReviewsSnippets
                    snippets={listing.reviews.snippets.filter(snippet => snippet.type === 'cons')}
                    snippetSize={snippetSize}
                    type="cons"
                  />
                </Paper>
              </GridItem>
            )
          })}
        </GridContainer>
        <GridContainer>
          {listings.map((listing, index) => {
            return (
              <GridItem className="UserReviews__Content" key={`user-reviews-content-cons-${listing.id}`}>
                <Paper square className={clsx(classes.paper, classes.bottomRounded)} elevation={0}>
                  <div className={classes.AttributeListContainer}>
                    <AttributeList
                      attributes={[
                        {
                          label: 'Vendor responds to reviews',
                          variant: listing.reviews.vendor_responses
                            ? ATTRIBUTE_VARIANTS.check
                            : ATTRIBUTE_VARIANTS.uncheck,
                        },
                      ]}
                      gridIndex={index}
                      gridSize={listings.length}
                    />
                  </div>
                  <div className={classes.AttributeListContainer}>
                    <AttributeList
                      attributes={[
                        {
                          text:
                            listing.reviews.snippets &&
                            listing.reviews.last_review &&
                            listing.reviews.last_review.length > 0
                              ? dayjs(listing.reviews.last_review).fromNow()
                              : 'N/A',
                          label: 'Last review',
                          variant: ATTRIBUTE_VARIANTS.since,
                        },
                      ]}
                      gridIndex={index}
                      gridSize={listings.length}
                    />
                  </div>
                  <div className={classes.reviewsButtonContainer} />
                </Paper>
              </GridItem>
            )
          })}
        </GridContainer>
      </EventContext>
    </>
  )
}

export default Reviews
